import type { ClientResponse } from "hono/client";
import type { TranslationKey } from "@msuite/utils/locales";
import { localized } from "@msuite/ui/localization";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { ResponseError } from "@msuite/utils/errors";
import type { Annotation, FileCollectionTable, Tables, UploadResult } from "@msuite/utils/types";
import { client } from "@/hono";

/**
 * This function is a utility hook to upload files to the server.
 * @returns
 */
export function useUpload() {
	/** Functions */
	async function uploadFn(body: FormData) {
		const response = await fetch(`${process.env.PUBLIC_SERVER_URL}/files`, {
			method: "POST",
			credentials: "include",
			body,
		});
		if (!response.ok) {
			toast.error(localized("general.something_went_wrong"));
		}
		const json = (await responseHandler(response as any)) as {
			success: boolean;
			results: UploadResult[] | null;
		};
		return json.results;
	}

	return { uploadFn };
}

/**
 * Hand a batch of dropped objects to the server, which records them and puts each one in the queue
 * to be filed.
 *
 * The answer is the batch id, which is what the pill watches: the classification rows arrive over
 * the sync path as the worker writes them, so nothing is polled and nothing is refetched.
 *
 * @param uploadResults - What the upload endpoint returned for this drop.
 * @returns The batch id to follow, or null when the server recorded nothing.
 */
export async function submitDroppedFiles(uploadResults: UploadResult[]) {
	const response = await client.files.dropzone.$post({ json: { uploadResults } });
	const data = await responseHandler(response);
	return data.batchId;
}

/**
 * Read the dropped files nothing could file.
 *
 * There is no other way to this list: the file explorer only ever mounts against one parent
 * entity, and a file that was never filed has no parent to mount against.
 *
 * @returns The unfiled files, newest first.
 */
export async function getFileInbox() {
	const response = await client.files.inbox.$get();
	const data = await responseHandler(response);
	return data.files;
}

/**
 * Read what became of every file of one drop, filed or not.
 *
 * The inbox answers "what is still outstanding" across every drop; this answers "what happened to
 * mine", which is the only account the filed half ever gets — the pill reports a count and closes.
 *
 * @param batchId - The upload id the drop produced.
 * @returns Every file of the batch, in the order they were dropped.
 */
export async function getFileBatch(batchId: string) {
	const response = await client.files.batches[":batchId"].$get({ param: { batchId } });
	const data = await responseHandler(response);
	return data.files;
}

/**
 * File one inbox file onto the record the user picked.
 *
 * @param fileId - The inbox file being filed.
 * @param tableName - The collection to file it into.
 * @param foreignKeyValues - The parent ids to write.
 */
export async function fileInboxEntry(
	fileId: string,
	tableName: FileCollectionTable,
	foreignKeyValues: Record<string, string>,
) {
	const response = await client.files.inbox[":fileId"].$post({
		param: { fileId },
		json: { tableName, foreignKeyValues },
	});
	await responseHandler(response);
}

/**
 * Resolves a signed URL for an already-uploaded file so it can be shown as a
 * preview default value in a `useFileUpload` controller.
 *
 * @param file - The file record to resolve a signed URL for.
 * @returns The signed URL, or `null` if it could not be resolved.
 */
export async function getRemoteFileUrl(file: Tables<"files">) {
	const response = await client.files[":fileId"].$get({ param: { fileId: file.id } });
	const data = await responseHandler(response);
	return data.signedUrl ?? null;
}

/**
 * Fetches a file's dimensions and its persisted annotations, shaped for the lightbox
 * image annotator.
 *
 * Annotating writes to the file, and a file's writes follow the parent it hangs on — so whether the
 * annotator offers editing is the surface's answer, not the file's, and the surface passes it in.
 * Left out, the annotator is editable and the server decides on save: a surface that does not know
 * which parent a file hangs on has nothing to refuse it with.
 *
 * @param fileId - The ID of the file to load annotations for.
 * @param canEdit - Whether the caller may write what hangs on the file's parent.
 * @returns The file record, its annotation payload, and whether the caller may draw on it.
 */
export async function getFileAnnotations(fileId: string, canEdit = true) {
	const response = await client.files[":fileId"].$get({ param: { fileId } });
	const data = await responseHandler(response);
	return {
		file: data.file,
		annotations: data.annotations,
		canEdit: !!data.file && canEdit,
	};
}

/**
 * Persists the given annotations for a file.
 *
 * @param fileId - The ID of the file to save annotations for.
 * @param annotations - The full set of annotations to upsert.
 */
export async function saveFileAnnotations(fileId: string, annotations: Annotation[]) {
	const response = await client.files[":fileId"].annotations.$post({
		param: { fileId },
		json: { annotations },
	});
	await responseHandler(response);
}

/**
 * Returns the subset of the given file IDs that currently have saved annotations,
 * in a single batch request (avoids an N+1 fetch per preview thumbnail).
 *
 * @param fileIds - The file IDs to check.
 * @returns The IDs that have non-empty annotations.
 */
export async function getFilesWithAnnotations(fileIds: string[]) {
	if (!fileIds.length) return [];
	const response = await client.files.annotations.exists.$post({ json: { fileIds } });
	const data = await responseHandler(response);
	return data.fileIds;
}

/**
 * Removes a single junction-table reference to a file (e.g. one `ticketFiles` row) without
 * deleting the underlying file, so it stays available for any other entity it's still linked
 * to. Use this instead of a hard delete whenever a file can be linked from more than one place.
 *
 * @param tableName - The junction table to delete from (e.g. `"ticketFiles"`, `"noteFiles"`).
 * @param key - The foreign key column identifying the parent record (e.g. `"ticketId"`).
 * @param identifier - The parent record's id.
 * @param fileId - The id of the file to unlink.
 */
export async function unlinkFile(
	tableName: FileCollectionTable,
	key: string,
	identifier: string,
	fileId: string,
) {
	const response = await client.files[":tableName"][":key"][":identifier"][":fileId"].$delete({
		param: { tableName, key, identifier, fileId },
	});
	await responseHandler(response);
}

/**
 * Links files to a junction-table row for an already-existing parent record (e.g. a ticket or
 * note), either by uploading new files (`uploadResults`), by pointing at already-uploaded ones
 * (`existingFileIds`, e.g. picked from a project gallery), or both at once. Accepts the full set
 * of foreign key values, since some junction tables (e.g. `ticketFiles`, which also requires
 * `projectId`) have more than one required foreign key column.
 *
 * @param tableName - The junction table to link into (e.g. `"ticketFiles"`, `"noteFiles"`).
 * @param foreignKeyValues - The foreign key column/value pairs identifying the parent record.
 * @param payload - The results of a raw file upload and/or the ids of already-uploaded files to link.
 * @returns The newly created junction rows.
 */
export async function linkFiles(
	tableName: FileCollectionTable,
	foreignKeyValues: Record<string, string>,
	payload: { uploadResults?: UploadResult[]; existingFileIds?: string[] },
) {
	const response = await client.files[":tableName"].$post({
		param: { tableName },
		json: { foreignKeyValues, ...payload },
	});
	const data = await responseHandler(response);
	return data.files;
}

/**
 * What a batch route names in the two answers that carry file rows: `refusedFiles` when the
 * caller's rule stopped the whole selection, `failedFiles` when storage would not move some of it.
 */
interface BatchRefusal {
	refusedFiles?: Array<{ id: string; fileName: string }>;
	failedFiles?: Array<{ id: string; fileName: string }>;
}

/**
 * Remove a selection of files, or none of them.
 *
 * `DELETE /files/batch` applies the caller's rule to every file and refuses the whole selection when
 * one of them fails, answering with the rows that stopped it.
 *
 * @param fileIds - The files the user selected.
 */
export async function deleteFileBatch(fileIds: string[]) {
	const response = await client.files.batch.$delete({ json: { fileIds } });
	await completeBatch(response, "files.batch_delete_refused");
}

/**
 * Move a selection of files into one folder.
 *
 * The route answers twice over: a 403 naming the files the caller's rule refused, in which case
 * nothing moved at all, or a 409 naming the files the target folder already held a name for, in
 * which case the rest of the selection did move.
 *
 * @param fileIds - The files the user selected.
 * @param newPath - The folder to move them into.
 */
export async function moveFileBatch(fileIds: string[], newPath: string) {
	const response = await client.files.batch.path.$patch({ json: { fileIds, newPath } });
	await completeBatch(response, "files.batch_move_refused", "files.batch_move_failed");
}

/**
 * Read a batch route's answer, naming the files it could not act on.
 *
 * The generic handler would localise the refusal to "forbidden" and drop the rows, which leaves the
 * user with no way to tell which of forty selected files stopped the whole thing. Anything that is
 * neither the whole-or-nothing refusal nor the per-file conflict goes back to it unchanged.
 */
async function completeBatch(
	response: ClientResponse<unknown, number, "json">,
	refusalKey: TranslationKey,
	conflictKey?: TranslationKey,
) {
	if (response.status === 403) await reportNamedFiles(response, "refusedFiles", refusalKey, 403);
	if (conflictKey && response.status === 409) {
		await reportNamedFiles(response, "failedFiles", conflictKey, 409);
	}
	await responseHandler(response as never);
}

/**
 * Toast the named message with the file names the body carries, and throw, so the caller's
 * mutation fails rather than reporting a success the server did not give.
 */
async function reportNamedFiles(
	response: ClientResponse<unknown, number, "json">,
	field: keyof BatchRefusal,
	key: TranslationKey,
	status: number,
) {
	const payload = (await response
		.clone()
		.json()
		.catch(() => null)) as BatchRefusal | null;
	const names = payload?.[field]?.map((file) => file.fileName) ?? [];
	if (!names.length) return;
	const message = localized(key, { files: names.join(", ") });
	toast.error(message);
	throw new ResponseError(message, status);
}
